<template lang="pug">
h1.mb-4 {{ $t("comments") }} {{ commentsLoadingState.loaded ? `(${commentsLoadingState.total})` : "" }}
.flex.justify-center(
  v-if="commentsLoadingState.loading && result.length === 0"
)
  .loading.h-32.w-32(v-loading="true")
.error(v-else-if="commentsLoadingState.error")
.comments.mb-8(v-else) 
  .flex.justify-center
    el-button(v-if="hasMorePages", @click="loadPreviousComments") {{ $t("loadPreviousComments") }}
  CommentItem(
    v-for="comment in commentsLoadingState.result",
    :key="comment.uuid",
    :comment="comment"
  )
.comment-input
  el-input.mb-4(
    v-model="commentInput",
    :rows="5",
    type="textarea",
    :placeholder="$t('comment')"
  )
  .flex.justify-end
    el-button(
      v-loading="commentSendLoading",
      :disabled="!isSendCommentAvailable",
      @click="sendComment"
    ) {{ $t("send") }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
  type PaginatedLoadState,
  stateInitial,
  stateLoading,
  stateError,
  stateLoaded,
} from "../../models/state";
import {
  getComments,
  type Comment,
  postComment,
} from "@shared/services/comments";
import { type PaginationQuery } from "@shared/services/common";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import CommentItem from "./CommentItem.vue";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";

const props = defineProps<{
  twinId: string;
}>();

const commentsLoadingState = ref<PaginatedLoadState<Comment[]>>({
  ...stateInitial,
  result: [],
});

const result = computed(() => commentsLoadingState.value.result || []);

const hasMorePages = computed(
  () => commentsLoadingState.value.pagesTotal > page.page,
);

const page: PaginationQuery = {
  page: 1,
  pageSize: 100,
};

async function loadData() {
  commentsLoadingState.value = { ...stateLoading, result: [] };
  const response = await getComments({ twin: props.twinId, ...page }).catch(
    (e) => {
      commentsLoadingState.value = {
        ...stateError,
        result: commentsLoadingState.value.result,
      };
      throw e;
    },
  );
  commentsLoadingState.value = {
    ...stateLoaded,
    ...response.data,
    result: response.data.result.reverse(),
  };
}

// NOTE: for now we only allow 100 comments, see https://github.com/inctec/smx-portal/issues/164 for details

async function loadPreviousComments() {
  page.page = page.page + 1;
  commentsLoadingState.value = {
    ...stateLoading,
    result: commentsLoadingState.value.result,
  };
  const response = await getComments({ twin: props.twinId, ...page }).catch(
    (e) => {
      commentsLoadingState.value = {
        ...stateError,
        result: commentsLoadingState.value.result,
      };
      throw e;
    },
  );
  const ids = new Set();
  const result = [
    ...response.data.result.reverse(),
    ...commentsLoadingState.value.result,
  ].filter((item) => {
    if (!ids.has(item.uuid)) {
      ids.add(item.uuid);
      return true;
    }
    return false;
  });

  commentsLoadingState.value = {
    ...stateLoaded,
    ...response.data,
    result: result,
  };
}

onMounted(() => {
  loadData();
});

watch(
  () => props.twinId,
  () => {
    page.page = 1;
    loadData();
  },
);

// Add comment
const { t } = useI18n();

const commentInput = ref("");

const commentSendLoading = ref(false);

const isSendCommentAvailable = computed(
  () => commentInput.value !== "" && !commentSendLoading.value,
);

function addCommentToList(comment) {
  commentsLoadingState.value.result.push(comment);
  commentsLoadingState.value.total += 1;
}

defineExpose({ reload: loadData });

async function sendComment() {
  if (!isSendCommentAvailable.value) {
    return;
  }
  const response = await postComment({
    twinUuid: props.twinId,
    content: commentInput.value,
  })
    .catch((e) => {
      showResponseErrorToUser(e, t);

      throw e;
    })
    .finally(() => {
      commentSendLoading.value = false;
    });

  commentInput.value = "";
  addCommentToList(response.data);
}
</script>

<style scoped>
.comments::v-deep > * + * {
  margin-top: 1rem;
}
</style>
